import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ActiveBadge } from '@/components/Badges';
import { EmptyState, LoadingState } from '@/components/States';
import Pagination from '@/components/Pagination';
import { Plus, Search, Edit2, Trash2, UserSquare2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { BR_STATES } from '@/lib/labels';
import { maskDocument, maskPhone, onlyDigits } from '@/lib/format';
import { CepInput } from '@/components/ui/cep-input';

const EMPTY = { name: '', document: '', email: '', phone: '', address: '', number: '', complement: '', city: '', state: '', zipCode: '', isActive: true };

export default function CustomersPage() {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('SUPER_ADMIN');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [stateF, setStateF] = useState('all');
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  const load = useCallback(async (page = 1) => {
    // Super Admin precisa selecionar empresa primeiro
    if (isSuperAdmin && !selectedCompany) {
      setItems([]);
      setMeta({ page: 1, pageSize: 10, total: 0, totalPages: 0 });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const params = { page, pageSize: 10 };
      if (q) params.q = q;
      if (stateF !== 'all') params.state = stateF;
      if (isSuperAdmin && selectedCompany) params.companyId = selectedCompany;
      const { data } = await api.get('/customers', { params });
      setItems(data.items);
      setMeta({ page: data.page, pageSize: data.pageSize, total: data.total, totalPages: data.totalPages });
    } catch { toast.error('Falha ao carregar clientes'); }
    finally { setLoading(false); }
  }, [q, stateF, isSuperAdmin, selectedCompany]);

  useEffect(() => {
    if (isSuperAdmin) {
      // Carregar lista de empresas
      api.get('/companies', { params: { page: 1, pageSize: 1000 } })
        .then(({ data }) => setCompanies(data.items || []))
        .catch(() => toast.error('Erro ao carregar empresas'));
    }
  }, [isSuperAdmin]);

  useEffect(() => { load(1); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpenModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...c, document: maskDocument(c.document), phone: maskPhone(c.phone) });
    setOpenModal(true);
  };

  const handleAddressFound = (addressData) => {
    setForm(prev => ({
      ...prev,
      zipCode: addressData.zipCode,
      address: addressData.address,
      city: addressData.city,
      state: addressData.state,
    }));
    
    // Focar no campo número se endereço foi preenchido
    if (addressData.address) {
      setTimeout(() => {
        document.querySelector('input[name="number"]')?.focus();
      }, 100);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, document: onlyDigits(form.document), phone: onlyDigits(form.phone), zipCode: onlyDigits(form.zipCode) };
      if (editing) await api.patch(`/customers/${editing.id}`, payload);
      else await api.post('/customers', payload);
      toast.success(editing ? 'Cliente atualizado' : 'Cliente criado');
      setOpenModal(false); load(meta.page);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/customers/${confirmDel.id}`); toast.success('Cliente excluído'); setConfirmDel(null); load(meta.page); }
    catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><UserSquare2 className="w-6 h-6 text-blue-600" /> Clientes</h1>
          <p className="text-sm text-slate-500">Sua base de clientes/consumidores.</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" disabled={isSuperAdmin && !selectedCompany}><Plus className="w-4 h-4 mr-2" /> Novo Cliente</Button>
      </div>

      {isSuperAdmin && (
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium whitespace-nowrap">Selecionar Empresa:</Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Selecione uma empresa..." />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.tradeName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Buscar por nome, documento, e-mail..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={stateF} onValueChange={setStateF}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder="UF" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos UF</SelectItem>
              {BR_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState title="Nenhum cliente" icon={UserSquare2} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">Documento</th>
                  <th className="px-4 py-3 font-medium">Contato</th>
                  <th className="px-4 py-3 font-medium">Cidade/UF</th>
                  {isSuperAdmin && <th className="px-4 py-3 font-medium">Empresa</th>}
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 text-slate-700 font-mono text-xs">{maskDocument(c.document)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{c.email || '-'}</div>
                      <div className="text-xs text-slate-500">{maskPhone(c.phone)}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{c.city ? `${c.city}/${c.state || '-'}` : '-'}</td>
                    {isSuperAdmin && <td className="px-4 py-3 text-slate-700">{c.company?.tradeName || '-'}</td>}
                    <td className="px-4 py-3"><ActiveBadge active={c.isActive} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDel(c)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination {...meta} onPageChange={(p) => load(p)} />
      </Card>

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader><DialogTitle>{editing ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>CPF/CNPJ</Label><Input value={form.document} onChange={(e) => setForm({ ...form, document: maskDocument(e.target.value) })} /></div>
              <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })} /></div>
            </div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <CepInput
                value={form.zipCode}
                onChange={(value) => setForm({ ...form, zipCode: value })}
                onAddressFound={handleAddressFound}
              />
              <div className="col-span-2"><Label>Endereço</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rua, Avenida..." /></div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label>Número</Label><Input name="number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="123" /></div>
              <div className="col-span-3"><Label>Complemento</Label><Input value={form.complement} onChange={(e) => setForm({ ...form, complement: e.target.value })} placeholder="Apto, Bloco, Sala..." /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label>Cidade</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Cidade" /></div>
              <div><Label>UF</Label>
                <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                  <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                  <SelectContent>{BR_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Status</Label>
              <Select value={form.isActive ? 'true' : 'false'} onValueChange={(v) => setForm({ ...form, isActive: v === 'true' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Ativo</SelectItem>
                  <SelectItem value="false">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando</> : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Excluir cliente?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{confirmDel?.name} será desativado.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancelar</Button>
            <Button onClick={remove} className="bg-red-600 hover:bg-red-700">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
